import { useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from '@/lib/supabase/client';
import { Message } from '../types';

const MESSAGES_PER_PAGE = 50;

/**
 * Hook to fetch and manage messages in a chat with realtime updates
 */
export const useMessages = (chatId: string, userId?: string) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const offsetRef = useRef(0);

  const fetchMessages = async (loadMore = false) => {
    if (!chatId) return;

    try {
      if (loadMore) {
        setLoadingMore(true);
      } else {
        setLoading(true);
        offsetRef.current = 0;
      }
      setError(null);

      const from = loadMore ? offsetRef.current : 0;
      const to = from + MESSAGES_PER_PAGE - 1;

      const { data, error: fetchError, count } = await supabase
        .from('messages')
        .select(`
          *,
          sender:profiles!sender_id(id, full_name, nickname, avatar_url),
          reactions:message_reactions(
            id, emoji, user_id,
            user:profiles(id, full_name, avatar_url)
          )
        `, { count: 'exact' })
        .eq('chat_id', chatId)
        .order('created_at', { ascending: false })
        .range(from, to);

      if (fetchError) throw fetchError;

      const newMessages = (data || []).reverse();

      if (loadMore) {
        setMessages((prev) => [...newMessages, ...prev]);
      } else {
        setMessages(newMessages);
      }

      offsetRef.current = to + 1;
      setHasMore((count || 0) > offsetRef.current);
    } catch (err) {
      console.error('Error fetching messages:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch messages');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    fetchMessages();

    // Subscribe to new messages
    const channel = supabase
      .channel(`messages:${chatId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'messages',
          filter: `chat_id=eq.${chatId}`,
        },
        async (payload: any) => {
          // Fetch full message with relations
          const { data } = await supabase
            .from('messages')
            .select(`
              *,
              sender:profiles!sender_id(id, full_name, nickname, avatar_url),
              reactions:message_reactions(
                id, emoji, user_id,
                user:profiles(id, full_name, avatar_url)
              )
            `)
            .eq('id', payload.new.id)
            .single();

          if (data) {
            setMessages((prev) => {
              // Deduplicate: replace optimistic message or skip if already exists
              if (prev.some((m) => m.id === data.id)) {
                return prev.map((m) => (m.id === data.id ? data : m));
              }
              return [...prev, data];
            });
          }
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'messages',
          filter: `chat_id=eq.${chatId}`,
        },
        async (payload: any) => {
          // Fetch updated message
          const { data } = await supabase
            .from('messages')
            .select(`
              *,
              sender:profiles!sender_id(id, full_name, nickname, avatar_url),
              reactions:message_reactions(
                id, emoji, user_id,
                user:profiles(id, full_name, avatar_url)
              )
            `)
            .eq('id', payload.new.id)
            .single();

          if (data) {
            setMessages((prev) =>
              prev.map((m) => (m.id === data.id ? data : m))
            );
          }
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'DELETE',
          schema: 'public',
          table: 'messages',
          filter: `chat_id=eq.${chatId}`,
        },
        (payload: any) => {
          setMessages((prev) => prev.filter((m) => m.id !== payload.old.id));
        }
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'message_reactions',
        },
        async (payload: any) => {
          const messageId = payload.new?.message_id || payload.old?.message_id;
          if (!messageId) return;
          const { data } = await supabase
            .from('messages')
            .select(`
              *,
              sender:profiles!sender_id(id, full_name, nickname, avatar_url),
              reactions:message_reactions(
                id, emoji, user_id,
                user:profiles(id, full_name, avatar_url)
              )
            `)
            .eq('id', messageId)
            .single();
          if (data) {
            setMessages((prev) =>
              prev.map((m) => (m.id === messageId ? data : m))
            );
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [chatId]);

  /**
   * Mark chat as read
   */
  const markAsRead = async () => {
    if (!userId || !chatId || messages.length === 0) return;

    try {
      const lastMessage = messages[messages.length - 1];

      await supabase
        .from('chat_participants')
        .update({
          last_read_at: new Date().toISOString(),
          last_read_message_id: lastMessage.id,
        })
        .eq('chat_id', chatId)
        .eq('user_id', userId);
    } catch (err) {
      console.error('Error marking as read:', err);
    }
  };

  /**
   * Load more older messages
   */
  const loadMore = () => {
    if (!loadingMore && hasMore) {
      fetchMessages(true);
    }
  };

  /**
   * Add a message optimistically (e.g. after sending)
   * Deduplicates by ID to prevent double rendering
   */
  const addOptimisticMessage = useCallback((message: Message) => {
    setMessages((prev) => {
      if (prev.some((m) => m.id === message.id)) return prev;
      return [...prev, message];
    });
  }, []);

  /**
   * Update a message in state (e.g. after adding/removing reaction)
   */
  const updateMessage = useCallback((messageId: string, updater: (m: Message) => Message) => {
    setMessages((prev) =>
      prev.map((m) => (m.id === messageId ? updater(m) : m))
    );
  }, []);

  return {
    messages,
    loading,
    loadingMore,
    hasMore,
    error,
    refetch: () => fetchMessages(false),
    loadMore,
    markAsRead,
    addOptimisticMessage,
    updateMessage,
  };
};

